import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import RecipeCard from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Heart, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export const FavoritesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        loadFavorites();
    }, [user, navigate]);

    const loadFavorites = async () => {
        setLoading(true);
        try {
            const response = await api.getFavorites();
            setFavorites(response.data);
        } catch (error) {
            console.error('Error loading favorites:', error);
            toast.error('Erreur lors du chargement des favoris');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveFavorite = async (recipeId) => {
        try {
            await api.removeFavorite(recipeId);
            setFavorites(prev => prev.filter(r => r.id !== recipeId));
            toast.success('Retiré des favoris');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    return (
        <div className="min-h-screen py-8 md:py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                            <Heart className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                                Mes favoris
                            </h1>
                            <p className="text-muted-foreground">
                                Vos recettes préférées sauvegardées
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : favorites.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                            <Heart className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                            Aucun favori pour l'instant
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            Explorez nos recettes et sauvegardez vos préférées!
                        </p>
                        <Button
                            onClick={() => navigate('/recipes')}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                        >
                            Découvrir des recettes
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="text-muted-foreground mb-6">
                            {favorites.length} recette{favorites.length > 1 ? 's' : ''} sauvegardée{favorites.length > 1 ? 's' : ''}
                        </p>
                        
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {favorites.map((recipe, index) => (
                                <div key={recipe.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                                    <RecipeCard
                                        recipe={recipe}
                                        isFavorite={true}
                                        onFavoriteToggle={handleRemoveFavorite}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default FavoritesPage;
